// GA4 analytics helpers for cloud-store.
// Only the basic, generic tracking methods are kept here (business-specific
// tracking from beyond-style has been intentionally removed).

// `window.gtag` is already declared globally in src/dts/global.d.ts.

type GtagParams = Record<string, unknown>;

/**
 * Send a generic GA4 event.
 * @param event  Event name (defaults to "click").
 * @param params Extra event parameters recorded in the event detail.
 */
export function trackEvent(event: string = "click", params: GtagParams = {}): void {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch (e) {
    // swallow analytics errors so they never break the UI
  }
}

/**
 * Convenience wrapper for a generic click event.
 */
export function trackClick(params: GtagParams = {}): void {
  trackEvent("click", params);
}

/**
 * Track a top menu click. Records which menu item was clicked in the detail.
 * @param menuItem Human readable label of the menu item.
 * @param params   Optional extra parameters.
 */
export function trackMenuClick(menuItem: string, params: GtagParams = {}): void {
  trackEvent("menu_click", {
    event_category: "navigation",
    menu_item: menuItem,
    ...params,
  });
}

/**
 * Track a "Contact Us" button click. Records which module the click came from.
 * @param module Source module / section where the button lives.
 * @param params Optional extra parameters.
 */
export function trackContactClick(module: string, params: GtagParams = {}): void {
  trackEvent("contact_us_click", {
    event_category: "engagement",
    source_module: module,
    ...params,
  });
}

/**
 * Track an article "Read More" click. Records the formatted article name,
 * which is also used as the article id and URL slug.
 * @param articleName Formatted article name (== article id / URL slug).
 * @param params      Optional extra parameters.
 */
export function trackArticleReadMore(articleName: string, params: GtagParams = {}): void {
  trackEvent("article_read_more_click", {
    event_category: "engagement",
    article_name: articleName,
    ...params,
  });
}

/**
 * Optionally associate a user/profile id with GA4 events.
 */
export function setGAIdentity(userId?: string, profileId?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("set", {
        user_id: userId,
        gid: profileId,
      });
    }
  } catch (e) {
    // ignore
  }
}
